import { ContractType } from '@app/api/models/Contracts';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button } from '../../../components/Buttons/Button';
import Input from '../../../components/Forms/Input';
import Label from '../../../components/Forms/Label';
import CardWithDivider from '../../../components/Layout/CardWithDivider';
import { Loading } from '../../../components/Loading/Loading';
import CardTitle from '../../../components/typography/CardTitle';
import { L } from '../../../lib/i18n';
import { useGetServicePartners } from '../../service-partner/hooks/servicePartnerQueries';
import useDeleteContract from '../hooks/useDeleteContract';
import useDeleteContractDocument from '../hooks/useDeleteContractDocument';
import useSaveContract from '../hooks/useSaveContract';
import useSaveContractDocument from '../hooks/useSaveContractDocument';
import useSaveEditedDocument from '../hooks/useSaveEditedDocument';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import { getContractKeys, useGetContractById, useGetTypes } from '../hooks/useContract';
import DocumentsAreaWithUpload from '../../../components/document/DocumentsAreaWithUpload';
import Contracts from '@app/api/public/ContractsApi';
import { style } from '../../../components/Shared/Style';
import ErrorAndInputWrapper from '../../../components/Forms/ErrorAndInputWrapper';
import { useQueryClient } from 'react-query';
import DropDownCreatable from '../../../components/DropDownCreatable/DropDownCreatable';
import { useModal } from '../../../components/Modal/Context/ModalContext';
import TypeModal from './TypeModal';
import Dialog from '../../../components/Dialog/Dialog';
import ServicePartnerForm from '../../service-partner/components/ServicePartnerForm/ServicePartnerForm';
import ConfirmationButton from '../../../components/Buttons/ConfirmationButton';
import { useHistory } from 'react-router-dom';
import useArchiveContract from '../hooks/useArchiveContract';

const StyledCardWithDivider = styled(CardWithDivider)({
    width: 'fit-content',
});

const Textarea = styled.textarea({
    width: '100%',
    borderRadius: '4px',
    backgroundColor: '#FCFDFE',
    border: '1px solid #F0F1F7',
    height: '125px',
    resize: 'none',
    padding: '8px',
    fontSize: '14px',
    '&:focus': {
        outline: 'none',
    },
});

const LabelWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const ButtonWrapper = styled.div({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const StyledButton = styled(Button)({
    marginRight: 0,
});

const CardContent = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const RadioButtonWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem',
    ['@media screen and (min-width: 1024px)']: {
        width: '20%',
    },
});

const RadioLabelWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
});

const GridContainer = styled.div({
    display: 'grid',
    gap: '1rem',
    columnGap: '2rem',
    backgroundColor: style.colors.white,
    alignItems: 'start',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gridTemplateAreas: `
    "contractor  contractor  contractor  statusOption  statusOption  statusOption"
    "name name name ... ... ..."
    "description  description  description description description description"
    "start_date  start_date  surveillance_date  surveillance_date  period_of_notice  period_of_notice"
    "contract-length-title  contract-length-title  contract-length-title  contract-length-title  contract-length-title  contract-length-title"
    "radio-wrapper  radio-wrapper  radio-wrapper  radio-wrapper  radio-wrapper  radio-wrapper"
    "documents  documents  documents  documents  documents  documents"
    "...  ...  ...  ...  buttons buttons"
    `,
    paddingBottom: '0px',
    maxWidth: '600px',
});

type FormValues = {
    name: string;
    description: string;
    start_date: string;
    end_date: string;
    notification_date: string;
    period_of_notice: string;
    length_months: string;
    statusOption: ContractType;
    servicePartner: { name: string; id: number };
    activeContractLengthOption: 'until_further_notice' | 'months' | 'end_date';
};

const EditContract = () => {
    const params = useParams<{ id: string }>();
    const { data: contract, isLoading } = useGetContractById(Number(params.id));
    const { data: statusOptions } = useGetTypes();
    const { mutate: saveContract } = useSaveContract();
    const { mutate: saveDocument } = useSaveContractDocument();
    const { mutate: updateDoc } = useSaveEditedDocument();
    const { mutate: deleteContract } = useDeleteContract();
    const { mutate: deleteDocument } = useDeleteContractDocument();
    const { mutate: archiveContract } = useArchiveContract();
    const { data: servicePartners } = useGetServicePartners();
    const queryClient = useQueryClient();
    const { ToggleIsModalOpen } = useModal();
    const [newType, setNewType] = useState('');
    const { push } = useHistory();

    const [open, setOpen] = useState(false);
    const [servicePartnerTextInput, setServicePartnerTextInput] = useState('');

    const history = useHistory();

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm<FormValues>();
    useEffect(() => {
        register('servicePartner', { required: true });
        register('statusOption', { required: true });
    }, [register]);

    useEffect(() => {
        if (!contract || !servicePartners) return;
        setValue('name', contract.name);
        setValue('description', contract.description);
        setValue('start_date', dayjs(contract.start_date).format('YYYY-MM-DD'));
        if (contract.end_date) setValue('end_date', dayjs(contract.end_date).format('YYYY-MM-DD'));
        setValue(
            'notification_date',
            contract.notification_date ? dayjs(contract.notification_date).format('YYYY-MM-DD') : ''
        );
        setValue('period_of_notice', contract.period_of_notice ?? '');
        setValue('length_months', contract.contract_length?.length_months ?? '');
        const servicePartner = servicePartners.find((partner) => contract.service_partner.id === partner.id);
        if (servicePartner) setValue('servicePartner', servicePartner);
        if (contract.contract_types) setValue('statusOption', contract.contract_types);
        if (contract.contract_length?.length_months) setValue('activeContractLengthOption', 'months');
        if (contract.contract_length?.until_further_notice)
            setValue('activeContractLengthOption', 'until_further_notice');
        if (contract.contract_length?.end_date) setValue('activeContractLengthOption', 'end_date');
    }, [contract]);
    if (!contract || !servicePartners) return <Loading />;

    const statusOptionValue = watch('statusOption');
    const activeContractLengthOptionValue = watch('activeContractLengthOption');
    const servicePartnerValue = watch('servicePartner');

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        const {
            activeContractLengthOption,
            description,
            end_date,
            length_months,
            name,
            notification_date,
            period_of_notice,
            servicePartner,
            start_date,
            statusOption,
        } = data;

        if (!servicePartner) return;
        if (!statusOption) return;
        saveContract(
            {
                contract: {
                    name,
                    description,
                    start_date,
                    period_of_notice,
                    notification_date,
                    id: contract.id,
                    service_partner: servicePartner.id,
                    contract_types: {
                        id: statusOption?.id,
                        name: statusOption?.name,
                        color_code: statusOption?.color_code,
                    },
                    contract_length: {
                        end_date: activeContractLengthOption === 'end_date' ? end_date : '',
                        until_further_notice: activeContractLengthOption === 'until_further_notice' ? true : false,
                        length_months: activeContractLengthOption === 'months' ? length_months : '',
                    },
                },
                dateChanged: new Date(contract.notification_date) !== new Date(notification_date),
            },
            {
                onSuccess: () => {
                    toast.success(L('updated_contract'));
                    queryClient.invalidateQueries(getContractKeys.getById(Number(params.id)));
                },
                onError: () => {
                    toast.error(L('contract_update_failed'));
                },
            }
        );
    };

    const createType = (e: string) => {
        ToggleIsModalOpen();
        setNewType(e);
    };

    const onArchive = () => {
        if (!contract.id) return;
        archiveContract(+contract.id, {
            onSuccess: (data) => {
                if (!data) return push('/contracts');
                if (data.archived_at) {
                    toast.success(`${L('contract_archived')}`);
                } else {
                    toast.success(`${L('contract_unarchived')}`);
                }
                push('/contracts');
            },
        });
    };

    const onRemove = () => {
        deleteContract(
            { id: contract.id },
            {
                onSuccess: () => {
                    toast.success(`${contract.name} ${L('removed')}`);
                    history.push('/contracts');
                },
            }
        );
    };

    if (isLoading) return <Loading />;

    return (
        <>
            <Dialog
                content={{
                    style: {
                        backgroundColor: '#FFFFFF',
                    },
                }}
                open={open}
                onOpenChange={setOpen}
            >
                <ServicePartnerForm
                    style={{ margin: 0 }}
                    submitOptios={{
                        onSuccess: (data) => {
                            toast.success(L('service_partner_added'));
                            setValue('servicePartner', { id: data.id, name: data.name });
                            setOpen(false);
                        },
                        onError: () => {
                            toast.success(L('error_occured'));
                        },
                    }}
                    initialFormData={{ name: servicePartnerTextInput }}
                />
            </Dialog>
            <TypeModal initial={newType} />
            <StyledCardWithDivider
                topArea={
                    <CardTitle>
                        <CardContent>
                            <span>{L('edit_contract_card')}</span>
                            <div style={{ display: 'flex', columnGap: style.margin.s }}>
                                <ConfirmationButton
                                    props={{
                                        onConfirm: onArchive,
                                    }}
                                >
                                    {contract.archived_at ? L('unarchive_ticket') : L('archive_ticket')}
                                </ConfirmationButton>
                                <ConfirmationButton props={{ onConfirm: onRemove }} />
                            </div>
                        </CardContent>
                    </CardTitle>
                }
                mainArea={
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <GridContainer>
                            <LabelWrapper style={{ gridArea: 'name' }}>
                                <Label title={`${L('name')}*`} />

                                <ErrorAndInputWrapper
                                    isMinHeight={false}
                                    errorMsg={
                                        errors.name?.type === 'maxLength'
                                            ? `${L('max_characters')} 40`
                                            : errors.name?.type === 'required'
                                            ? L('error_required')
                                            : ''
                                    }
                                >
                                    <Input
                                        style={errors?.name ? { borderColor: '#ff4545' } : {}}
                                        placeholder={L('name')}
                                        type="text"
                                        {...register('name', { required: true, maxLength: 40 })}
                                    />
                                </ErrorAndInputWrapper>
                            </LabelWrapper>

                            <LabelWrapper style={{ gridArea: 'contractor' }}>
                                <Label title={`${L('contractor')}*`} />
                                <ErrorAndInputWrapper
                                    isMinHeight={false}
                                    errorMsg={errors.servicePartner?.type === 'required' ? L('error_required') : ''}
                                >
                                    <DropDownCreatable
                                        onChange={(value) => {
                                            if (!value) return;
                                            setValue('servicePartner', { id: value.value, name: value.label });
                                        }}
                                        value={
                                            servicePartnerValue
                                                ? { label: servicePartnerValue.name, value: servicePartnerValue.id }
                                                : undefined
                                        }
                                        options={
                                            servicePartners?.map((partner) => {
                                                return { value: partner.id, label: (L(partner.name) as string) ?? '' };
                                            }) || []
                                        }
                                        placeholder={L('select')}
                                        css
                                        onCreateOption={(value) => {
                                            setServicePartnerTextInput(value);
                                            setOpen(true);
                                        }}
                                    />
                                </ErrorAndInputWrapper>
                            </LabelWrapper>

                            <LabelWrapper style={{ gridArea: 'statusOption' }}>
                                <Label title={`${L('type')}*`} />
                                <ErrorAndInputWrapper
                                    isMinHeight={false}
                                    errorMsg={errors.statusOption?.type === 'required' ? L('error_required') : ''}
                                >
                                    <DropDownCreatable
                                        placeholder={L('select')}
                                        isClearable
                                        value={
                                            statusOptionValue?.name
                                                ? { value: statusOptionValue, label: L(statusOptionValue?.name) }
                                                : undefined
                                        }
                                        options={
                                            statusOptions?.map((status) => {
                                                return { value: status, label: L(status.name) };
                                            }) || []
                                        }
                                        onChange={(value) =>
                                            setValue('statusOption', {
                                                id: Number(value?.value.id),
                                                name: String(value?.value.name),
                                            })
                                        }
                                        onCreateOption={createType}
                                    />
                                </ErrorAndInputWrapper>
                            </LabelWrapper>

                            <LabelWrapper style={{ gridArea: 'description' }}>
                                <Label title={L('description')} />
                                <Textarea placeholder={L('description')} {...register('description')} />
                            </LabelWrapper>

                            <LabelWrapper style={{ gridArea: 'period_of_notice' }}>
                                <Label title={`${L('notice_period_months')}`} titleVariant="noWhiteSpaceWrap" />
                                <ErrorAndInputWrapper
                                    isMinHeight={false}
                                    errorMsg={
                                        errors.period_of_notice?.type === 'min'
                                            ? L('amount_greater_than_0')
                                            : errors.period_of_notice?.type === 'pattern'
                                            ? errors.period_of_notice.message
                                            : ''
                                    }
                                >
                                    <Input
                                        placeholder={`${L('number_of_months')}`}
                                        type="number"
                                        min={0}
                                        {...register('period_of_notice', {
                                            min: 0,
                                            pattern: {
                                                value: /^[0-9]+$/,
                                                message: L('integer_only_error'),
                                            },
                                        })}
                                        onBlur={(e) => e.target.valueAsNumber < 0}
                                        style={errors?.period_of_notice ? { borderColor: '#ff4545' } : {}}
                                    />
                                </ErrorAndInputWrapper>
                            </LabelWrapper>

                            <LabelWrapper style={{ gridArea: 'surveillance_date' }}>
                                <Label title={`${L('surveillance_date')}`} />
                                <Input
                                    placeholder={`${L('surveillance_date')}`}
                                    type="date"
                                    {...register('notification_date')}
                                    style={errors?.notification_date ? { borderColor: '#ff4545' } : {}}
                                />
                            </LabelWrapper>
                            <LabelWrapper style={{ gridArea: 'start_date' }}>
                                <Label title={`${L('start_date')}*`} />
                                <ErrorAndInputWrapper
                                    isMinHeight={false}
                                    errorMsg={errors.start_date?.type === 'required' ? L('error_required') : ''}
                                >
                                    <Input
                                        style={errors?.start_date ? { borderColor: '#ff4545' } : {}}
                                        placeholder={`${L('start_date')}*`}
                                        type="date"
                                        {...register('start_date', { required: true })}
                                    />
                                </ErrorAndInputWrapper>
                            </LabelWrapper>

                            <Label style={{ gridArea: 'contract-length-title ' }} title={L('contract_length')} />

                            <RadioButtonWrapper style={{ gridArea: 'radio-wrapper' }}>
                                <RadioLabelWrapper>
                                    <Controller
                                        name={'activeContractLengthOption'}
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                value="until_further_notice"
                                                type="radio"
                                                checked={activeContractLengthOptionValue === 'until_further_notice'}
                                            />
                                        )}
                                    />

                                    <Label title={L('until_further_notice')} />
                                </RadioLabelWrapper>

                                <RadioLabelWrapper>
                                    <Controller
                                        name={'activeContractLengthOption'}
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                value="months"
                                                type="radio"
                                                checked={activeContractLengthOptionValue === 'months'}
                                            />
                                        )}
                                    />
                                    <Label title={L('number_of_months')} titleVariant="noWhiteSpaceWrap" />
                                </RadioLabelWrapper>

                                {activeContractLengthOptionValue === 'months' && (
                                    <ErrorAndInputWrapper
                                        isMinHeight={false}
                                        errorMsg={
                                            errors.length_months?.type === 'min'
                                                ? L('amount_greater_than_0')
                                                : errors.length_months?.type === 'pattern'
                                                ? errors.length_months.message
                                                : errors.length_months?.type === 'required'
                                                ? L('error_required')
                                                : ''
                                        }
                                    >
                                        <Input
                                            style={errors?.length_months ? { borderColor: '#ff4545' } : {}}
                                            min={0}
                                            placeholder={L('number_of_months')}
                                            type="number"
                                            {...register('length_months', {
                                                required: true,
                                                min: 0,
                                                pattern: {
                                                    value: /^[0-9]+$/,
                                                    message: L('integer_only_error'),
                                                },
                                            })}
                                        />
                                    </ErrorAndInputWrapper>
                                )}
                                <RadioLabelWrapper>
                                    <Controller
                                        name={'activeContractLengthOption'}
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                type="radio"
                                                value={'end_date'}
                                                checked={activeContractLengthOptionValue === 'end_date'}
                                            />
                                        )}
                                    />
                                    <Label title={L('end_date')} />
                                </RadioLabelWrapper>

                                {activeContractLengthOptionValue === 'end_date' && (
                                    <ErrorAndInputWrapper
                                        isMinHeight={false}
                                        errorMsg={errors.end_date?.type === 'required' ? L('error_required') : ''}
                                    >
                                        <Input
                                            style={errors?.end_date ? { borderColor: '#ff4545' } : {}}
                                            placeholder={L('end_date')}
                                            type="date"
                                            {...register('end_date', { required: true })}
                                        />
                                    </ErrorAndInputWrapper>
                                )}
                            </RadioButtonWrapper>
                            <DocumentsAreaWithUpload
                                style={{ gridArea: 'documents' }}
                                documentQueryKey={getContractKeys.documentsList(params.id)}
                                getDocuments={async () =>
                                    (await Contracts.listDocuments(params.id)).map((doc) => ({
                                        filename: doc.document_data.original.metadata.filename,
                                        id: doc.id,
                                        name: doc.name,
                                        originalId: doc.document_data.original.id,
                                    }))
                                }
                                removeDocument={async (documentId) =>
                                    deleteDocument({
                                        documentId: String(documentId),
                                        queryId: params.id,
                                    })
                                }
                                renameDocument={async (documentId, newName) =>
                                    updateDoc({
                                        documentName: newName,
                                        documentId: String(documentId),
                                        queryId: params.id,
                                    })
                                }
                                saveDocument={async (file) =>
                                    saveDocument({ itemId: params.id, name: file.name, file })
                                }
                            />
                            <ButtonWrapper style={{ gridArea: 'buttons' }}>
                                <StyledButton variant="selectedMenu" type="submit">
                                    {L('save')}
                                </StyledButton>
                            </ButtonWrapper>
                        </GridContainer>
                    </form>
                }
            />
        </>
    );
};

export default EditContract;
